<template>
    <header v-if="!$route.path.includes('sessions') && !$route.path.includes('register')"
        class="bg-slate-900 text-white sticky top-0 z-50 shadow-md border-b border-slate-800">
        <div class="max-w-[1640px] mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
            <!-- Brand + desktop nav -->
            <div class="flex items-center gap-8 min-w-0">
                <router-link to="/web/home/dashboard" class="flex items-center gap-2.5 shrink-0">
                    <img :src="logo" alt="VGC" class="w-9 h-9 rounded-lg bg-white/5 object-contain p-1">
                    <span class="hidden sm:flex flex-col leading-tight">
                        <span class="font-bold text-lg tracking-wider text-white">VGC</span>
                        <span class="text-[10px] text-teal-400 font-medium tracking-widest uppercase">Client
                            Portal</span>
                    </span>
                </router-link>

                <nav aria-label="Main Navigation" class="hidden xl:flex items-center gap-1">
                    <div v-for="item in footer" :key="item.name" class="relative"
                        @mouseenter="item.children && (openMenu = item.name)"
                        @mouseleave="item.children && (openMenu = null)">
                        <button type="button"
                            class="px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 border-0"
                            :class="isActive(item) ? 'bg-white/10 text-teal-300' : 'bg-transparent text-slate-300 hover:text-white hover:bg-white/5'"
                            @click="onNavClick(item)">
                            {{ $t(item.name) }}
                            <svg v-if="item.children" class="w-3.5 h-3.5 opacity-60" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path d="M19 9l-7 7-7-7" stroke-linecap="round" stroke-linejoin="round"
                                    stroke-width="2" />
                            </svg>
                        </button>
                        <div v-if="item.children" v-show="openMenu === item.name"
                            class="absolute left-0 top-full pt-2 min-w-[200px] z-50">
                            <div class="bg-white rounded-xl shadow-lg border border-slate-100 py-1.5 text-left">
                                <router-link v-for="child in item.children" :key="child.name" :to="child.path"
                                    class="block px-4 py-2 text-sm font-medium text-slate-600 hover:text-sky-700 hover:bg-sky-50 transition-colors"
                                    @click.native="openMenu = null">
                                    {{ $t(child.name) }}
                                </router-link>
                            </div>
                        </div>
                    </div>
                </nav>
            </div>

            <!-- Right utility bar -->
            <div class="flex items-center gap-2 sm:gap-3 shrink-0">
                <button type="button"
                    class="hidden xl:flex items-center bg-slate-800 hover:bg-slate-700/80 border border-slate-700 text-xs rounded-full px-3 py-1.5 transition"
                    @click="$router.push('/web/accounts/trading_accounts')">
                    <span class="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse"></span>
                    <template v-if="primaryAccount">
                        <span class="text-slate-300 font-medium">{{ primaryAccount.group_title || 'MT5' }}
                            #<strong class="text-white">{{ primaryAccount.mt5_login }}</strong></span>
                        <span class="mx-2 text-slate-600">|</span>
                        <span class="text-teal-400 font-semibold">${{ formatNumber(primaryAccount.balance) }}
                            USD</span>
                    </template>
                    <span v-else class="text-slate-300 font-medium">{{ userInfo.username }}</span>
                </button>

                <a href="mailto:support@vgcfx.com" title="在线客服"
                    class="hidden xl:inline-flex p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-full transition">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                            stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                    </svg>
                </a>

                <button type="button" title="系统通知" @click="$router.push('/web/home/news')"
                    class="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-full transition relative bg-transparent border-0">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                            stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                    </svg>
                    <span v-if="$root.admin_replied > 0"
                        class="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {{ $root.admin_replied }}
                    </span>
                </button>

                <div class="relative lang-switcher">
                    <button type="button" @click.stop="langOpen = !langOpen"
                        class="p-1.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg text-xs flex items-center gap-1 border border-slate-700/60 bg-transparent">
                        <img :src="require(`../assets/images/flags/${$i18n.locale}.png`)" alt="" height="16px">
                        <span class="font-medium text-slate-200 hidden sm:inline">{{ $t($i18n.locale) }}</span>
                    </button>
                    <div v-show="langOpen"
                        class="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-slate-100 py-1 min-w-[130px] z-50 text-left">
                        <button v-for="option in $i18n.availableLocales" :key="option" type="button"
                            class="w-100 bg-transparent border-0 text-left px-3 py-2 text-sm text-slate-600 hover:bg-sky-50 hover:text-sky-700 flex items-center gap-2"
                            @click="selectLang(option)">
                            <img :src="require(`../assets/images/flags/${option}.png`)" alt="" height="16px">
                            <span>{{ $t(option) }}</span>
                        </button>
                    </div>
                </div>

                <div class="hidden xl:flex items-center gap-2.5 pl-2 border-l border-slate-700">
                    <div
                        class="w-8 h-8 rounded-full bg-gradient-to-r from-sky-600 to-indigo-600 flex items-center justify-center text-white font-semibold text-xs border border-white/20 uppercase">
                        {{ (userInfo.username || '').substring(0, 2) }}
                    </div>
                    <div class="hidden xl:flex flex-col text-left">
                        <span class="text-xs font-semibold text-white leading-tight">{{ userInfo.username }}</span>
                        <span class="text-[10px] text-slate-400">UID: {{ userInfo.member_code || userInfo.id }}</span>
                    </div>
                    <button type="button" class="text-slate-400 hover:text-red-400 transition ml-1 bg-transparent border-0" title="退出登录"
                        @click="logout">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                        </svg>
                    </button>
                </div>

                <button type="button" class="xl:hidden p-2 text-slate-300 bg-transparent border-0" v-b-toggle.sidebar-right>
                    <img src="../assets/images/actions/hamburger.png" alt="" height="22px" width="22px">
                </button>
            </div>
        </div>
    </header>
</template>
<script>
export default {
    props: ['footer'],
    data() {
        return {
            openMenu: null,
            langOpen: false,
            logo: require('../assets/images/vgc_logo.png'),
        };
    },
    mounted() {
        document.addEventListener('click', this.closeMenus);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeMenus);
    },
    computed: {
        userInfo() {
            return this.$root.userInfo;
        },
        primaryAccount() {
            return this.$root.mt5Accounts && this.$root.mt5Accounts.length ? this.$root.mt5Accounts[0] : null;
        },
    },
    methods: {
        formatNumber(value) {
            return parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        isActive(item) {
            return this.$route.path.startsWith(item.path) || (item.children && item.children.some(child => this.$route.path.startsWith(child.path)));
        },
        onNavClick(item) {
            if (!item.children) {
                this.$router.push(item.path);
            } else {
                this.openMenu = this.openMenu === item.name ? null : item.name;
            }
        },
        logout() {
            localStorage.removeItem('currencyToken');
            localStorage.removeItem('info');
            this.$router.push('/web/sessions/login');
        },
        selectLang(option) {
            this.$root.changeLan(option);
            this.langOpen = false;
        },
        closeMenus(e) {
            if (!this.$el.contains(e.target)) {
                this.langOpen = false;
                this.openMenu = null;
            }
        },
    },
}
</script>
